import { TFunction } from "i18next";
import { modal } from "Components/General/Modal/AntdModal";
import { ShowModalFunction } from "Components/General/Modal/ModalContext";
import { LinkToUnifiedAccountModal } from "Pages/Login/Pages/Login/Components/LinkToUnifiedAccountModal";
import { loginSettingsData } from "Pages/Login/Stores/LoginSettingsStore";

/**
 * Returns true when the login response requires showing the unified-account
 * link/switch prompt. Extracted so callers (password login + MFA) share the
 * same predicate without copy-pasting the condition.
 */
export const needsUnifiedAccountLinkPrompt = (response: API_LoginInformation): boolean => {
  if (!loginSettingsData.data.unifiedAccountEnabled) return false;
  if (response.status !== "OK") return false;
  const notLinked = response.hasUnifiedAccount === false && response.unifiedAccount === false;
  const linkedButNotUsing = response.hasUnifiedAccount === true && response.unifiedAccount === false;
  return notLinked || linkedButNotUsing;
};

export interface PromptUnifiedAccountLinkingOptions {
  response: API_LoginInformation;
  t: TFunction;
  showModal: ShowModalFunction;
  /** Skip / Continue with current (non-PAVE) account */
  onContinueWithoutLinking: () => void | Promise<void>;
  /** After creating a new PAVE account or choosing "Login with PAVE account" */
  onPreparePaveRelogin: (paveEmail: string) => void;
}

/**
 * Shows the appropriate confirm dialog (and modal) for linking/switching to a
 * PAVE unified account. Shared between password login and MFA flows.
 */
export const promptUnifiedAccountLinking = (options: PromptUnifiedAccountLinkingOptions): void => {
  const { response, t, showModal, onContinueWithoutLinking, onPreparePaveRelogin } = options;

  if (response.hasUnifiedAccount === false && response.unifiedAccount === false) {
    modal.confirm({
      title: t("Your account is not linked to a PAVE account"),
      content: t("Would you like to link your account to a PAVE account?"),
      okText: t("Link account"),
      cancelText: t("Skip"),
      onOk: () => {
        showModal(LinkToUnifiedAccountModal, {
          modalProps: {
            title: t("Link account to PAVE account"),
            footer: null,
            width: 600,
            maskClosable: false,
          },
          componentProps: {
            loginInfo: response,
            onPaveUserCreated: (linkedAccountEmail: string) => {
              onPreparePaveRelogin(linkedAccountEmail);
            },
          },
        });
      },
      onCancel: () => {
        onContinueWithoutLinking();
      },
    });
    return;
  }

  if (response.hasUnifiedAccount === true) {
    modal.confirm({
      title: t("Your account is linked to a PAVE account"),
      content: t(
        // eslint-disable-next-line max-len
        "Would you like to login with your PAVE account? If you skip, you will be logged in with your current account."
      ),
      okText: t("Login with PAVE account"),
      cancelText: t("Continue"),
      onOk: () => {
        onPreparePaveRelogin(response.unifiedAccountName);
      },
      onCancel: () => {
        onContinueWithoutLinking();
      },
    });
  }
};
